import * as eff from 'redux-saga/effects';
import createActionCreators from 'redux-resource-action-creators';
import {
  getStatus,
} from 'redux-resource';

import {
  actionTypes,
  setProfileState,
} from '../actions';
import {
  getProfile,
} from '../selectors';
import {
  throwError,
} from './helpers';
import api from '../api';

export default function* profileFlow(reportChannel) {
  function* fetchProfile() {
    try {
      const profile = yield eff.call(
        api.getProfile,
      );
      yield eff.put(
        reportChannel,
        {
          message: profile,
          messageType: 'fetchProfileSuccess',
        },
      );

      if (profile.namespace === 'ideafit') {
        yield fetchNamespace({ namespaceSymbol: profile.namespace })
      }

      yield eff.put(setProfileState({
        ...profile,
        isAuth: true,
        isLoading: false,
      }));
    } catch (err) {
      yield eff.put(setProfileState({
        isAuth: false,
        isLoading: false,
      }));
      yield eff.call(throwError, err);
    }
  }

  function* fetchNamespace({ namespaceSymbol }: { namespaceSymbol: string }) {
    try {
      const namespace = yield eff.call(
        api.getUserNamespace,
        {

          symbol: namespaceSymbol,
        },
      );

      yield eff.put({
        type: 'ui/SET_NAMESPACE_SETTINGS',
        payload: namespace.settings,
      })

    } catch (err) {
      yield eff.call(throwError, err);
    }
  }

  function* getDashboards({ isPro }) {
    try {
      const dashboard = yield eff.call(
        api.getDashboards,
        {
          type: isPro ? 'pro' : 'client',
          qs: isPro ? undefined : {
            dashboard_type: 'webapp',
          }
        },
      );

      yield eff.put(setProfileState({
        dashboard,
      }));
    } catch (e) {
      yield eff.call(throwError, e);
    }
  }

  function* getClientsPerformance() {
    const actions = createActionCreators('read', {
      resourceType: 'clientsPerformance',
      requestKey: 'fetchPerformance',
    });

    const readStatus = yield eff.select(
      getStatus,
      'clientsPerformance.requests.fetchPerformance.status',
    );

    try {
      if (readStatus.idle) {
        yield eff.put(actions.pending());
      }

      const resources = yield eff.call(
        api.getClientsPerformance,
      );

      yield eff.put(actions.succeeded({
        resources,
      }));
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.call(throwError, e);
    }
  }

  function* connectProvider({ provider, payload }) {
    const actions = createActionCreators('create', {
      resourceType: 'userAuthentications',
      requestKey: 'connectProvider',
    });
    yield eff.put(actions.pending());
    try {
      const { auth } = yield eff.call(
        api.connectWithProvider,
        {
          provider,
          body: payload,
          requestParams: {
            withCredentials: true,
          },
        },
      );
      const id = yield eff.select(
        getProfile('id'),
      );
      yield eff.put(actions.succeeded({
        resources: [auth],
        list: `client_${id}`,
      }));
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.call(throwError, e);
    }
  }

  function* updateUser({
    body,
    userId,
    callbacks,
  }) {
    const actions = createActionCreators('update', {
      resourceType: 'clients',
      requestKey: 'updateUserSettings',
    });
    yield eff.put(actions.pending());

    try {
      const me = yield eff.select(
        getProfile('id'),
      );

      const response = yield eff.call(
        api.updateEntity,
        {
          id: userId || me,
          type: 'users',
          body,
        },
      );

      if (userId !== me) {
        yield eff.put(actions.succeeded({
          resources: [response],
        }));
      } else {
        yield eff.put(setProfileState(response));

        yield eff.put(actions.succeeded({
          resources: [],
        }));
      }

      callbacks?.setSubmitting?.(false);
      callbacks?.onSuccess?.(response);
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.call(throwError, e);

      callbacks?.setSubmitting?.(false);
      callbacks?.onError?.(e);
    }
  }

  function* updateSettings({
    body,
    clientId,
    settingsId,
    callbacks,
  }) {
    const actions = createActionCreators('update', {
      resourceType: 'clients',
      requestKey: 'updateUserSettings',
    });
    yield eff.put(actions.pending());

    try {
      const response = yield eff.call(
        api.updateEntity,
        {
          id: settingsId,
          type: 'user_settings',
          body,
        },
      );
      const profileId = yield eff.select(
        getProfile('id'),
      );

      if (clientId && clientId !== profileId) {
        yield eff.put(actions.succeeded({
          resources: [{
            id: clientId,
            user_settings: response,
          }],
        }));
      } else {
        yield eff.put(setProfileState({
          user_settings: response,
        }));

        yield eff.put(actions.succeeded({
          resources: [],
        }));
      }

      if (body.tutorial_steps?.length === 0) {
        yield eff.put(
          reportChannel,
          {
            messageType: 'notify',
            message: {
              type: 'success',
              key: 'user.profile.tutorial_reset',
            },
          },
        );
      }

      callbacks?.setSubmitting?.(false);
      callbacks?.onSuccess?.(response);
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.call(throwError, e);

      callbacks?.setSubmitting?.(false);
      callbacks?.onError?.(e);
    }
  }

  function* markTutorialStepsComplite({
    steps,
    callbacks,
  }) {
    try {
      const settings = yield eff.select(
        getProfile('user_settings'),
      );
      const userSteps = settings.tutorial_steps;
      const response = yield eff.call(
        api.updateEntity,
        {
          id: settings.id,
          type: 'user_settings',
          body: {
            tutorial_steps: [...new Set([...userSteps, ...steps])],
          },
        },
      );

      yield eff.put(
        setProfileState({
          user_settings: response,
        }),
      );

      callbacks?.setSubmitting?.(false);
      callbacks?.onSuccess?.(response);
    } catch (e) {
      yield eff.call(throwError, e);

      callbacks?.setSubmitting?.(false);
      callbacks?.onError?.(e);
    }
  }

  // initial
  yield eff.fork(fetchProfile);

  yield eff.takeEvery(actionTypes.REQUEST_FETCH_PROFILE, fetchProfile);
  yield eff.takeEvery(actionTypes.REQUEST_DASHBOARDS, getDashboards);
  yield eff.takeEvery(actionTypes.REQUEST_USER_NAMESPACE, fetchNamespace);
  yield eff.takeEvery(actionTypes.REQUEST_CLIENTS_PERFORMANCE, getClientsPerformance);
  yield eff.takeEvery(actionTypes.REQUEST_CONNECT_PROVIDER, connectProvider);
  yield eff.takeEvery(actionTypes.REQUEST_UPDATE_USER, updateUser);
  yield eff.takeEvery(actionTypes.REQUEST_UPDATE_SETTINGS, updateSettings);
  yield eff.takeEvery(actionTypes.REQUEST_MARK_TUTOTIAL_STEPS_COMPLITE, markTutorialStepsComplite);
}
